import React from "react";
import { UserType } from "types/UsersTypes";
import { Button, SVG, EmptyState, SelectComponent } from "shared";
import {
  useGetAdminSuccessors,
  useSuccessorConnection,
} from "hooks/successors";
import {
  SuccessorType,
  SuccessorDocs,
  successorTypeMap,
} from "types/SuccessorTypes";
import moment from "moment";
import { SVG_ENUM } from "enums";
import { downloadFile } from "utils";
import { useResetSuccessorPasswordByAdmin } from "hooks/successors";
import { Field, Formik, Form } from "formik";
import { useGetClientsOptions } from "hooks/users";
import "./SuccessorTable.scss";

interface SuccesorTableProps {
  user: UserType | null;
}

const SuccesorTable: React.FC<SuccesorTableProps> = ({ user }) => {
  const { data } = useGetAdminSuccessors({ uuid: user?.uuid });
  const { data: clientOptions } = useGetClientsOptions(true);
  const { mutate: resetPassword } = useResetSuccessorPasswordByAdmin();
  const { mutateAsync: connectSuccessor, isPending } = useSuccessorConnection();
  const successors = data?.successors || [];
  const accessibleDocuments = data?.accessibleDocuments || [];
  const isSuccessorAccess = data?.isConfigured;
  const isSuccessorAllowedShowFile = data?.canViewFiles;
  const handleDownloadFile = (doc: SuccessorDocs) => {
    downloadFile({
      name: doc.file.name,
      url: doc.file.url,
    });
  };
  const handleResetPassword = async () => {
    if (user?.uuid) {
      await resetPassword({ uuid: user?.uuid });
    }
  };

  return (
    <>
      {isSuccessorAccess ? (
        <div className="successor-table">
          <>
            <h2 className="side-modal__table-heading successor-table__heading">
              Sukcesorzy
              <Button
                size="small"
                label="Wyślij link do resetu hasła"
                onClick={() => handleResetPassword()}
                className="button--stroke button--rounded"
              />
            </h2>
          </>
          {successors.length > 0 ? (
            <div className="successor-table__content-wrapper">
              {successors.map(
                ({
                  fullName,
                  nationalId,
                  phone,
                  email,
                  address,
                  postalCode,
                  city,
                  isPaid,
                  uuid,
                  successorId,
                }: SuccessorType) => {
                  const initialValues = {
                    userId: successorId,
                  };
                  return (
                    <div className="successor-table__content">
                      {!isPaid && (
                        <div className="successor-table__not-paid">
                          Nieopłacony
                        </div>
                      )}
                      <table>
                        <tr>
                          <td className="side-modal__table-label">
                            Imię i nazwisko:
                          </td>
                          <td className="side-modal__table-cell">{fullName}</td>
                        </tr>
                        <tr>
                          <td className="side-modal__table-label">Pesel:</td>
                          <td className="side-modal__table-cell">
                            {nationalId}
                          </td>
                        </tr>
                        {user?.email && (
                          <tr>
                            <td className="side-modal__table-label">
                              Telefon:
                            </td>
                            <td className="side-modal__table-cell">{phone}</td>
                          </tr>
                        )}
                        <tr>
                          <td className="side-modal__table-label">Email:</td>
                          <td className="side-modal__table-cell">{email}</td>
                        </tr>
                        <tr className={isPaid ? "succ-table-row__address" : ""}>
                          <td className="side-modal__table-label">Adres:</td>
                          <td className="side-modal__table-cell">
                            {address}, {postalCode} {city}
                          </td>
                        </tr>
                      </table>
                      {isPaid ? (
                        <>
                          <div className="succ-table-row__client side-modal__table-label">
                            Klient:
                          </div>
                          <Formik
                            initialValues={initialValues}
                            onSubmit={() => console.log({})}
                            enableReinitialize
                          >
                            {({ setFieldValue }) => (
                              <Form className="m-width-100 succ-table-row__form">
                                <Field
                                  name="userId"
                                  id="userId"
                                  placehoder="Wybierz klienta..."
                                  as={SelectComponent}
                                  options={clientOptions}
                                  isSearchable
                                  isClearable
                                  onSelectChange={(val: any) => {
                                    const selectedId =
                                      typeof val === "object" &&
                                      val?.value !== undefined
                                        ? val.value
                                        : val;
                                    setFieldValue("userId", selectedId);
                                    if (!isPending) {
                                      connectSuccessor({
                                        clientId:
                                          selectedId === null ||
                                          selectedId === undefined ||
                                          selectedId === ""
                                            ? null
                                            : Number(selectedId),
                                        successorUuid: uuid,
                                      });
                                    }
                                  }}
                                />
                              </Form>
                            )}
                          </Formik>
                        </>
                      ) : (
                        ""
                      )}
                    </div>
                  );
                }
              )}
            </div>
          ) : (
            <EmptyState
              className="succ-empty-state"
              svg={SVG_ENUM.INFO}
              text={<p>Użytkownik nie dodał jeszcze żadnego sukcesora.</p>}
            />
          )}

          <h2 className="side-modal__table-heading successor-table__heading">
            Dokumenty
          </h2>
          {accessibleDocuments.length > 0 ? (
            <div className="successor-table-documents">
              {accessibleDocuments.map((doc: SuccessorDocs) => {
                return (
                  <div className="succ-table-doc-item">
                    <div className="succ-table-doc-item__header">
                      <div className="succ-table-doc-item__type">
                        {successorTypeMap[doc.type]}
                      </div>
                      |
                      <div className="succ-table-doc-item__date">
                        {moment(doc.file.createdAt).format("DD.MM.YYYY")}
                      </div>
                    </div>
                    <div className="succ-table-doc-item__file-name">
                      {doc.file.name}
                    </div>
                    <div className="succ-table-doc-item__file-description">
                      {doc.description}
                    </div>
                    <div
                      className="succ-table-doc-item__download"
                      onClick={() => handleDownloadFile(doc)}
                    >
                      <SVG type={SVG_ENUM.FILE} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {isSuccessorAllowedShowFile ? (
                <EmptyState
                  className="succ-empty-state"
                  svg={SVG_ENUM.INFO}
                  text={
                    <p>
                      Użytkownik nie dodał jeszcze dokumentu który można
                      podejrzeć.
                    </p>
                  }
                />
              ) : (
                <EmptyState
                  className="succ-empty-state"
                  svg={SVG_ENUM.INFO}
                  text={
                    <p>Użytkownik nie zezwolił na podglądanie dokumentów</p>
                  }
                />
              )}
            </>
          )}
        </div>
      ) : (
        <EmptyState
          className="succ-empty-state"
          svg={SVG_ENUM.INFO}
          text={
            <p>Użytkownik nie skonfigurował dostępu do zakładki Sukcesja.</p>
          }
        />
      )}
    </>
  );
};

export default SuccesorTable;
